// The transaction screen's two layout switches, as visible segmented controls
// (they used to be a drop-down that also mixed editor layouts into the list's):
//
//   LayoutSwitch        — how the LIST is shown: List · Spread sheet · Bulk
//                         entry · Compact (+ Process where the object sits on a
//                         flow). Beside the tabs, or ending the toolbar row.
//   EditorLayoutSwitch  — how the open RECORD is edited: Standard · Simple ·
//                         Document · Compact. In every editor's header.
//
// Ids and labels come from listScreenConfig.json; which ids are OFFERED is the
// caller's (a host offers only what it can render). The choice is remembered
// per person by the screen (screenPrefs), not here.
//
// A person may also keep either switch OFF the screen — in its ⋮ menu
// (`layoutMenuItems` for the list, `editorLayoutMenuItems` for the editor's
// title line; place `menu`); the menu then offers the layouts as choices and a
// way to put the switch back. Where each sits is remembered per person and
// screen (screenPrefs `switch` / `editorSwitch`).

import type { ReactNode } from 'react'
import { ClipboardList, EyeOff, LayoutGrid, List, PanelTop, Sheet, Workflow } from 'lucide-react'
import { tw } from '../../testHooks'
import {
  editorLayoutLabel, listLayoutLabel, EDITOR_LAYOUTS, LAYOUT_SWITCH_PLACES,
  type EditorLayout, type LayoutSwitchPlace,
} from './listScreenConfig'
import type { MoreMenuItem } from '../../customize/ui'
import type { EmbedListView } from './InterfaceSwitcher'

const LIST_ICONS: Record<string, ReactNode> = {
  list: <List size={15} aria-hidden="true" />,
  tabular: <Sheet size={15} aria-hidden="true" />,
  'form-input': <ClipboardList size={15} aria-hidden="true" />,
  compact: <LayoutGrid size={15} aria-hidden="true" />,
  process: <Workflow size={15} aria-hidden="true" />,
}

const groupStyle: React.CSSProperties = {
  display: 'inline-flex', padding: 3, gap: 2, borderRadius: 9,
  // The track: the band tone, a step darker than the card it sits on.
  background: 'var(--color-surface-2, #f0f3f7)',
  boxShadow: 'inset 0 0 0 1px var(--color-border, #dde3ea)',
  flexWrap: 'wrap',
}

function segStyle(on: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    height: 30, padding: '0 10px', border: 0, borderRadius: 7,
    background: on ? 'var(--color-bg-secondary, #ffffff)' : 'transparent',
    color: on ? 'var(--color-text, #1c2430)' : 'var(--color-text-secondary, #465062)',
    boxShadow: on ? '0 1px 2px rgba(20,30,45,.14), 0 0 0 1px var(--color-border, #dde3ea)' : 'none',
    fontSize: '0.8125rem', fontWeight: on ? 600 : 500,
    whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
  }
}

/** How the list is shown. `compact` narrows it to icons (a phone toolbar). */
export function LayoutSwitch({
  value, options, onChange, iconsOnly = false,
}: {
  value: EmbedListView
  options: EmbedListView[]
  onChange: (v: EmbedListView) => void
  iconsOnly?: boolean
}) {
  if (options.length < 2) return null
  return (
    <div role="group" aria-label="List layout" style={groupStyle}
         onClick={(e) => e.stopPropagation()}>
      {options.map((id) => {
        const on = id === value
        const label = listLayoutLabel(id)
        return (
          <button key={id} type="button" aria-pressed={on}
                  title={iconsOnly ? label : undefined}
                  aria-label={iconsOnly ? label : undefined}
                  onClick={() => { if (!on) onChange(id) }}
                  {...tw('action', `layout-switch:${id}`)}
                  style={segStyle(on)}>
            {LIST_ICONS[id]}{!iconsOnly && label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The ⋮ menu's part of the list layout switch.
 *
 * - place `bar` (the switch is on the screen): one item that moves it into
 *   the menu;
 * - place `menu`: the layouts as choices (the current one checked) and one
 *   item that puts the switch back on the screen.
 *
 * Nothing when there is only one layout to offer.
 */
export function layoutMenuItems({
  value, options, onChange, place, onPlace,
}: {
  value: EmbedListView
  options: EmbedListView[]
  onChange: (v: EmbedListView) => void
  place: LayoutSwitchPlace
  onPlace: (p: LayoutSwitchPlace) => void
}): MoreMenuItem[] {
  if (options.length < 2) return []
  const placeLabel = (id: LayoutSwitchPlace) => LAYOUT_SWITCH_PLACES.find((p) => p.id === id)?.label || id
  if (place !== 'menu') {
    return [{
      label: 'Hide layout switch',
      icon: <EyeOff size={14} aria-hidden="true" />,
      onClick: () => onPlace('menu'),
      hook: `layout-switch-place:menu`,
    }]
  }
  return [
    ...options.map((id, i): MoreMenuItem => ({
      label: listLayoutLabel(id),
      icon: LIST_ICONS[id],
      checked: id === value,
      onClick: () => { if (id !== value) onChange(id) },
      hook: `layout-switch:${id}`,
      ...(i === 0 ? { heading: 'Layout' } : {}),
    })),
    {
      label: `Show layout switch ${placeLabel('bar').toLowerCase()}`,
      icon: <PanelTop size={14} aria-hidden="true" />,
      onClick: () => onPlace('bar'),
      hook: `layout-switch-place:bar`,
      divider: true,
    },
  ]
}

/**
 * The editor ⋮ menu's part of the EDITOR layout switch — the same shape as
 * `layoutMenuItems`: on the title line (`bar`) one item hides it; in the menu
 * (`menu`) the layouts as choices plus one item that puts it back.
 */
export function editorLayoutMenuItems({
  value, options, onChange, place, onPlace,
}: {
  value: EditorLayout
  options: EditorLayout[]
  onChange: (v: EditorLayout) => void
  place: LayoutSwitchPlace
  onPlace: (p: LayoutSwitchPlace) => void
}): MoreMenuItem[] {
  if (options.length < 2) return []
  const placeLabel = (id: LayoutSwitchPlace) => LAYOUT_SWITCH_PLACES.find((p) => p.id === id)?.label || id
  if (place !== 'menu') {
    return [{
      label: 'Hide layout switch',
      icon: <EyeOff size={14} aria-hidden="true" />,
      onClick: () => onPlace('menu'),
      hook: `editor-layout-place:menu`,
    }]
  }
  return [
    ...options.map((id, i): MoreMenuItem => ({
      label: editorLayoutLabel(id),
      checked: id === value,
      onClick: () => { if (id !== value) onChange(id) },
      hook: `editor-layout:${id}`,
      ...(i === 0 ? { heading: 'Layout' } : {}),
    })),
    {
      label: `Show layout switch ${placeLabel('bar').toLowerCase()}`,
      icon: <PanelTop size={14} aria-hidden="true" />,
      onClick: () => onPlace('bar'),
      hook: `editor-layout-place:bar`,
      divider: true,
    },
  ]
}

/** How the open record is edited. Remembered for this screen by the host. */
export function EditorLayoutSwitch({
  value, options, onChange,
}: {
  value: EditorLayout
  options: EditorLayout[]
  onChange: (v: EditorLayout) => void
}) {
  if (options.length < 2) return null
  return (
    <div role="group" aria-label="Editor layout" style={{ ...groupStyle, flexWrap: 'nowrap' }}
         title="Your choice is remembered for this screen"
         onClick={(e) => e.stopPropagation()}>
      {options.map((id) => {
        const on = id === value
        const hint = EDITOR_LAYOUTS.find((l) => l.id === id)?.hint
        return (
          <button key={id} type="button" aria-pressed={on} title={hint}
                  onClick={() => { if (!on) onChange(id) }}
                  {...tw('action', `editor-layout:${id}`)}
                  style={{ ...segStyle(on), height: 30, padding: '0 11px' }}>
            {editorLayoutLabel(id)}
          </button>
        )
      })}
    </div>
  )
}
